import VerifiedIcon from '~/src/components/svg/VerifiedIcon'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~/src/components/ui/tooltip'
import useAuthUser from '~/src/hooks/useAuthUser'
import {
  useBrandPartnerStats,
  type BrandPartnerStats,
} from '~/src/hooks/admin/useBrandPartnerStats'
import { cn } from '~/src/lib/utils'

interface Props {
  iconClassName?: string
  preferred?: string | boolean | null
  shopKey?: string
}

const VerifiedBadge: React.FC<Props> = ({
  iconClassName,
  preferred,
  shopKey,
}) => {
  const { isAdmin } = useAuthUser()
  const showAdminStats = isAdmin && !!shopKey

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <VerifiedIcon
            style={
              preferred && typeof preferred === 'string'
                ? {
                    color: preferred,
                  }
                : undefined
            }
            className={cn('text-verified h-2.5 w-2.5', iconClassName)}
          />
        </TooltipTrigger>
        <TooltipContent className='w-60 normal-case'>
          <p>
            Dupe.com may earn commissions from{' '}
            {preferred ? 'preferred partner' : 'partner'} brands.
          </p>
          {showAdminStats && <AdminPartnerStats shopKey={shopKey} />}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className='flex items-baseline justify-between gap-3 text-xs'>
    <span className='opacity-60'>{label}</span>
    <span className='font-medium tabular-nums'>{value}</span>
  </div>
)

const formatCurrency = (value: number) => {
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(value >= 10_000 ? 0 : 1)}k`
  }
  return `$${value.toFixed(value < 10 ? 2 : 0)}`
}

const formatRange = (range: BrandPartnerStats['commissionRate']) => {
  if (!range) return '—'
  const min = (range.min * 100).toFixed(1)
  const max = (range.max * 100).toFixed(1)
  return min === max ? `${min}%` : `${min}–${max}%`
}

const formatRealized = (rate: number | null) =>
  rate == null ? '—' : `${(rate * 100).toFixed(2)}%`

export const NETWORK_LABELS: Record<string, string> = {
  cj: 'CJ',
  flex_offers: 'FlexOffers',
  rakuten: 'Rakuten',
  awin: 'Awin',
  impact: 'Impact',
  pepperjam: 'Pepperjam',
  avantlink: 'AvantLink',
  linkconnector: 'LinkConnector',
  lc: 'LinkConnector',
  partnerize: 'Partnerize',
  connexity: 'Connexity',
  sovrn: 'Sovrn',
  amazon: 'Amazon',
  ebay: 'eBay',
  target: 'Target',
  bbb: 'BBB',
}

const formatNetworks = (networks: string[]) => {
  if (!networks.length) return '—'
  return networks.map((n) => NETWORK_LABELS[n] ?? n).join(' · ')
}

const AdminPartnerStats: React.FC<{ shopKey: string }> = ({ shopKey }) => {
  const { data, isLoading, error } = useBrandPartnerStats(shopKey)

  return (
    <div className='border-popover-foreground/10 mt-1.5 space-y-1 border-t pt-1.5'>
      {isLoading && <div className='text-xs opacity-60'>Loading…</div>}
      {error && !isLoading && (
        <div className='text-xs text-red-500'>Failed to load partner stats</div>
      )}
      {data && (
        <>
          <Row label='Network' value={formatNetworks(data.networks)} />
          <Row label='Program' value={formatRange(data.commissionRate)} />
          <Row label='Realized' value={formatRealized(data.realizedTakeRate)} />
          <Row label='GMV (30d)' value={formatCurrency(data.gmv30d)} />
          <Row label='Revenue (30d)' value={formatCurrency(data.revenue30d)} />
          <Row
            label='Transactions (30d)'
            value={(data.transactions30d ?? 0).toLocaleString()}
          />
          <Row
            label='Clicks (30d)'
            value={(data.clicks30d ?? 0).toLocaleString()}
          />
        </>
      )}
    </div>
  )
}

export default VerifiedBadge
